import { desaturate, mix } from "polished";

import { Color, Palette, Shadows } from "@mui/material";
import { Theme, createTheme } from "@mui/material/styles";

import {
  desaturatedGreen,
  desaturatedIndigo,
  desaturatedOrange,
  desaturatedRed,
} from "@/config";
import { DialogTransition } from "@/primitives";

import { printStyles } from "./print";

// Constants for maintainability
export const THEME_CONSTANTS = {
  borderRadius: 20,
  borderWidth: "0.75px",
  spacing: 10,
  desaturateAmount: {
    light: 0.1,
    medium: 0.2,
    heavy: 0.5,
    veryHeavy: 0.75,
  },
  lightenAmount: {
    bgDefault: 0.03,
    bgPaper: 0.9,
  },
};

// Generate shadows programmatically
const createShadows = (primaryColor: Color): Shadows => {
  const shadows: string[] = ["none"];
  const baseColor300 = desaturate(
    THEME_CONSTANTS.desaturateAmount.veryHeavy,
    primaryColor[300],
  );
  const baseColor400 = desaturate(
    THEME_CONSTANTS.desaturateAmount.veryHeavy,
    primaryColor[400],
  );

  for (let i = 1; i <= 24; i++) {
    const blur1 = i * 2;
    const spread1 = blur1 / 2;
    const opacity1 = 20 + i * 2.5;

    const blur2 = Math.floor(i / 2) + 1;
    const spread2 = blur2 / 2;
    const opacity2 = 20 + i;

    shadows.push(
      `0px ${blur1}px ${spread1 * 2}px ${baseColor300}${Math.floor(opacity1).toString(16)}, 0px ${blur2}px ${spread2 * 2}px ${baseColor400}${Math.floor(opacity2).toString(16)}`,
    );
  }

  return shadows as Shadows;
};

// Base theme - created once
const baseTheme: Theme = createTheme({
  shape: {
    borderRadius: THEME_CONSTANTS.borderRadius,
  },
});

const backgrounds = {
  paper: "#fff",
  default: mix(0.95, "#fff", desaturatedOrange[500]),
};

export const useBaikiTheme = () => {
  const primaryColor = desaturatedOrange;
  const secondaryColor = desaturatedIndigo;

  // Calculate colors once
  const divider = mix(0.75, "#eee", desaturatedIndigo[200]);
  const shadows = createShadows(primaryColor);

  const palette: Palette = {
    ...baseTheme.palette,
    background: backgrounds,
    divider,
    text: {
      primary: desaturate(0.66, desaturatedIndigo[900]),
      secondary: desaturate(0.66, desaturatedIndigo[600]),
      disabled: desaturate(0.66, desaturatedIndigo[300]),
    },
    error: {
      contrastText: "#fff",
      dark: desaturatedRed[900],
      light: desaturatedRed[300],
      main: desaturatedRed[600],
    },
    primary: {
      contrastText: "#fff",
      main: primaryColor[500],
      light: primaryColor[300],
      dark: primaryColor[700],
    },
    secondary: {
      contrastText: "#fff",
      dark: secondaryColor[700],
      light: secondaryColor[300],
      main: secondaryColor[500],
    },
    success: {
      contrastText: "#fff",
      dark: desaturatedGreen[900],
      light: desaturatedGreen[300],
      main: desaturatedGreen[600],
    },
  };

  return createTheme({
    ...baseTheme,
    shadows,
    spacing: THEME_CONSTANTS.spacing,
    palette,
    components: {
      MuiCssBaseline: {
        styleOverrides: `

          @property --angle {
            inherits: false;
            initial-value: 0deg;
            syntax: "<angle>";
          }

          :root {
            /* Define fallback CSS custom properties for browsers that don't support env() */
            --safe-area-inset-top: 0px;
            --safe-area-inset-right: 0px;
            --safe-area-inset-bottom: 0px;
            --safe-area-inset-left: 0px;
          }

          /* iOS-specific: Removed safe area insets to maximize screen real estate */
          @supports (padding-top: env(safe-area-inset-top)) {
            html {
              padding: 0;
            }
            body {
              padding: 0;
            }
          }

          html {
            height: 100%;
            overflow: hidden;
          }

          body {
            height: 100%;
            overflow: hidden;
            background-color: ${backgrounds.default};
          }

          #root {
            /* Make #root the scrolling container for iOS bouncy scroll */
            height: 100%;
            width: 100%;
            overflow-x: hidden;
            overflow-y: auto;
            /* Enable iOS momentum/bouncy scrolling on the root element */
            -webkit-overflow-scrolling: touch;
          }

          a {
            text-decoration: none;
          }

          img {
            margin: 0;
            line-height: 0;
          }

          em {
            font-style: initial;
            text-decoration: underline;
          }

          sup {
            font-size: 0.66em;
            line-height: 0;
            opacity: 0.5;
            position: relative;
            top: -0.5em;
            vertical-align: baseline;
          }

          ${printStyles}
        `,
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            alignItems: "center",
            gap: THEME_CONSTANTS.spacing * 1,
          },
          message: {
            textWrap: "balance",
            flexGrow: 1,
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            background: "none",
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            borderRadius: THEME_CONSTANTS.borderRadius * 2,
          },
          label: {
            color: palette.primary.main,
            fontWeight: 500,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            padding: baseTheme.spacing(1.2, 2.4),
            borderRadius: THEME_CONSTANTS.borderRadius * 2,
            textTransform: "none",
          },
          sizeSmall: {
            padding: baseTheme.spacing(0.8, 1.8),
          },
          sizeLarge: {
            padding: baseTheme.spacing(1.5, 3),
          },
        },
      },
      MuiCard: {
        defaultProps: {
          variant: "outlined",
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: baseTheme.shape.borderRadius,
          },
          sizeSmall: {
            minHeight: baseTheme.spacing(3),
            minWidth: baseTheme.spacing(3),
          },
          sizeMedium: {
            minWidth: baseTheme.spacing(4),
          },
          outlined: {
            borderWidth: THEME_CONSTANTS.borderWidth,
          },
          outlinedPrimary: {
            background: backgrounds.default,
            color: palette.primary.main,
            borderColor: palette.primary.main,
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          fixed: true,
        },
      },
      MuiDialog: {
        defaultProps: {
          slots: {
            transition: DialogTransition,
          },
          fullScreen: true,
          scroll: "paper",
        },
        styleOverrides: {
          paper: {
            background: backgrounds.default,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            opacity: 0.75,
            borderBottomWidth: THEME_CONSTANTS.borderWidth,
          },
          vertical: {
            borderRightWidth: THEME_CONSTANTS.borderWidth,
          },
          inset: {
            marginLeft: baseTheme.spacing(7),
            [baseTheme.breakpoints.up("md")]: {
              marginLeft: baseTheme.spacing(10),
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: baseTheme.spacing(1),
            color: palette.text.disabled,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            background: baseTheme.palette.background.paper,
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: "always",
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: baseTheme.shape.borderRadius,
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          slotProps: {
            paper: {
              variant: "outlined",
              elevation: undefined,
            },
          },
        },
        styleOverrides: {
          list: {
            padding: baseTheme.spacing(1.0, 1.0),
            minWidth: "200px",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: baseTheme.shape.borderRadius,
            fontWeight: "500",
            margin: baseTheme.spacing(0.25, 0),
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderWidth: "0.75px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          outlined: {
            border: `.75px solid ${divider}`,
            boxShadow: shadows[1],
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          thumb: {
            width: 16,
            height: 16,
            "&:hover, &.Mui-focusVisible": {
              boxShadow: `0px 0px 0px 8px ${desaturate(THEME_CONSTANTS.desaturateAmount.heavy, primaryColor[300])}28`,
            },
            "&.Mui-active": {
              boxShadow: `0px 0px 0px 12px ${desaturate(THEME_CONSTANTS.desaturateAmount.heavy, primaryColor[300])}32`,
            },
          },
          valueLabel: {
            background: palette.primary.dark,
            borderRadius: baseTheme.shape.borderRadius,
            color: palette.primary.contrastText,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: palette.primary.dark,
            borderRadius: baseTheme.shape.borderRadius,
            color: palette.primary.contrastText,
            fontSize: baseTheme.typography.pxToRem(12),
            lineHeight: "1.2rem",
            padding: baseTheme.spacing(0.5, 1),
            "@media (pointer: coarse)": {
              // Targets touch/primary coarse pointers
              display: "none !important",
            },
          },
          tooltipArrow: {
            background: palette.primary.main,
            color: palette.primary.contrastText,
          },
          arrow: {
            "&:before": {
              background: palette.primary.main,
            },
          },
        },
      },
    },
    typography: {
      fontFamily: "DM Sans, Helvetica, Arial, sans-serif",
      h1: {
        fontSize: baseTheme.typography.pxToRem(36),
        fontWeight: 700,
        letterSpacing: "-0.025em",
        lineHeight: "1.1em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(50),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(64),
        },
      },
      h2: {
        fontSize: baseTheme.typography.pxToRem(26),
        fontWeight: 600,
        lineHeight: "1.25em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(35),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(44),
        },
      },
      h3: {
        fontSize: baseTheme.typography.pxToRem(19),
        fontWeight: 600,
        lineHeight: "1.3em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(24),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(28),
        },
      },
      h4: {
        fontSize: baseTheme.typography.pxToRem(18),
        fontWeight: 600,
        lineHeight: "1.4em",
        letterSpacing: 0,
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(22),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(26),
        },
      },
      h5: {
        fontSize: baseTheme.typography.pxToRem(17),
        fontWeight: 600,
        lineHeight: "1.4em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(19),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(21),
        },
      },
      h6: {
        fontSize: baseTheme.typography.pxToRem(16),
        fontWeight: 600,
        lineHeight: "1.4em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(18),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(20),
        },
      },
      subtitle1: {
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: "1.5em",
        fontWeight: 500,
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(17),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(18),
        },
      },
      subtitle2: {
        fontSize: baseTheme.typography.pxToRem(15),
        lineHeight: "1.5em",
        fontWeight: 500,
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(16),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(17),
        },
      },
      body1: {
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: "1.5em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(17),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(18),
        },
      },
      body2: {
        fontSize: baseTheme.typography.pxToRem(15),
        lineHeight: "1.5em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(16),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(17),
        },
      },
      caption: {
        fontSize: baseTheme.typography.pxToRem(14),
        lineHeight: "1.4em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(15),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(16),
        },
      },
      overline: {
        letterSpacing: "0.2em",
        fontSize: baseTheme.typography.pxToRem(12),
        lineHeight: "1.4em",
        [baseTheme.breakpoints.up("md")]: {
          fontSize: baseTheme.typography.pxToRem(13),
        },
        [baseTheme.breakpoints.up("lg")]: {
          fontSize: baseTheme.typography.pxToRem(14),
        },
      },
      button: {
        fontWeight: 600,
        letterSpacing: 0,
      },
    },
  });
};
